import { useNavigate } from 'react-router-dom';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useAuth } from '@backrs_ui/grow-ui/src/hooks/useAuth';
import {
  hasChartableAssessment,
  isUnsupportedEnrollmentType,
} from '@backrs_ui/grow-ui/src/pages/AssessMyTrack/assessMyTrack.utils';
import { useAssessmentBreakdown } from '@backrs_ui/grow-ui/src/pages/AssessMyTrack/hooks/useAssessmentBreakdown';
import {
  CareerTrackTimelineChart,
  CHART_MAX_WIDTH,
} from '@backrs_ui/grow-ui/src/pages/AssessMyTrack/molecules/CareerTrackTimelineChart';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { useFetchUserProfileInfoQuery } from '@backrs_ui/grow-ui/src/redux/profile/profile';
import { USER_PROFILE_FIELDS } from '@backrs_ui/grow-ui/src/redux/profile/profile.constants';
import {
  useGetMostRecentTrackAssessmentResultQuery,
  useGetTrackAssessmentHistoryQuery,
} from '@backrs_ui/grow-ui/src/redux/trackAssessment/trackAssessment.api';
import { selectUser } from '@backrs_ui/grow-ui/src/redux/user/user';
import { getDemoAwareRoute } from '@backrs_ui/grow-ui/src/utils/demoUtils';

import { Box, SxProps, Theme } from '@mui/material';

import { HomePageCtaCard } from './HomePageCtaCard';

export const FeaturedHeader = () => {
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);
  const { isGuest } = useAuth();
  const mostRecentResponses = useAppSelector(
    (state) => state.userResponseSet.mostRecentUserResponseSet?.user_responses,
  );
  // Guest visitors who already finished a blueprint go to it (the signup gate) rather than redoing the survey.
  const guestHasBlueprint = isGuest && !!mostRecentResponses?.length;
  const blueprintCtaPath = getDemoAwareRoute(
    guestHasBlueprint ? APP_ROUTE.MY_BLUEPRINT : APP_ROUTE.SURVEY,
    isGuest,
  );
  const assessmentBreakdown = useAssessmentBreakdown();
  const { data: trackAssessmentResult } = useGetMostRecentTrackAssessmentResultQuery(undefined);
  const { data: assessmentHistory } = useGetTrackAssessmentHistoryQuery(undefined);
  const { data: profileData } = useFetchUserProfileInfoQuery(USER_PROFILE_FIELDS, {
    skip: !user?.id,
  });

  const enrollmentTypes = profileData?.profile.academicEnrollmentTypes;
  const isNonStudent = !!enrollmentTypes && isUnsupportedEnrollmentType(enrollmentTypes);

  if (isNonStudent) {
    return null;
  }

  if (!user?.has_blueprint) {
    return (
      <Box sx={containerSx}>
        <HomePageCtaCard
          illustration={
            <Box
              component="img"
              src="/images/homepage-blueprint-cta.png"
              alt=""
              sx={illustrationSx}
            />
          }
          title={appStrings.modules.home.blueprintCta.title}
          description={appStrings.modules.home.blueprintCta.description}
          ctaLabel={appStrings.modules.home.blueprintCta.ctaText}
          ctaPath={blueprintCtaPath}
        />
      </Box>
    );
  }

  if (!user?.has_track_assessment) {
    return (
      <Box sx={containerSx}>
        <HomePageCtaCard
          illustration={
            <Box component="img" src="/images/homepage-ayot-cta.png" alt="" sx={illustrationSx} />
          }
          title={appStrings.modules.home.trackAssessmentCta.title}
          description={appStrings.modules.home.trackAssessmentCta.description}
          ctaLabel={appStrings.modules.home.trackAssessmentCta.ctaText}
          ctaPath={APP_ROUTE.ASSESS_MY_TRACK}
        />
      </Box>
    );
  }

  if (!trackAssessmentResult || !assessmentHistory || !hasChartableAssessment(assessmentHistory)) {
    return <Box sx={containerSx} />;
  }

  return (
    <Box sx={containerSx}>
      <Box
        id="home-are-you-on-track-chart"
        onClick={() => navigate(APP_ROUTE.ASSESS_MY_TRACK)}
        sx={clickableChartSx}
      >
        <CareerTrackTimelineChart
          minimumTrack={assessmentBreakdown.minimumTrack}
          trackAssessmentResult={trackAssessmentResult}
          hideLegend
        />
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  pb: backrs.spacing['spacing-4xl'],
});

const clickableChartSx: SxProps<Theme> = ({ backrs }) => ({
  cursor: 'pointer',
  borderRadius: backrs.radius['radius-xl'],
  transition: 'box-shadow 0.3s ease-in-out, transform 0.3s ease-in-out',
  maxWidth: CHART_MAX_WIDTH,
  '&:hover': {
    boxShadow: backrs.shadows['shadow-lg'],
    transform: 'translate(-2px, -2px)',
  },
});

const illustrationSx: SxProps<Theme> = {
  width: '100%',
  height: 'auto',
  display: 'block',
};
